import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import type { Photo } from './types';

interface Props {
  photos: Photo[];
  index: number;
  onChange: (i: number) => void;
  onClose: () => void;
}

export function PhotoLightbox({ photos, index, onChange, onClose }: Props) {
  const total = photos.length;
  const current = photos[index];

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      else if (e.key === 'ArrowRight') onChange((index + 1) % total);
      else if (e.key === 'ArrowLeft') onChange((index - 1 + total) % total);
    };
    window.addEventListener('keydown', handleKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', handleKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [index, total, onChange, onClose]);

  if (!current) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[1000] flex items-center justify-center bg-ink-900/85 backdrop-blur-sm"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label="Photo viewer"
    >
      <button
        type="button"
        onClick={onClose}
        aria-label="Close photo viewer"
        className="absolute z-20 top-4 right-4 w-11 h-11 rounded-full bg-ink-900/60 hover:bg-ink-900/80 backdrop-blur-sm text-white flex items-center justify-center transition shadow-lg"
      >
        <svg width="16" height="16" viewBox="0 0 14 14" fill="none" aria-hidden>
          <path d="M1 1L13 13M13 1L1 13" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
        </svg>
      </button>

      {total > 1 && (
        <>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onChange((index - 1 + total) % total);
            }}
            aria-label="Previous photo"
            className="absolute z-20 left-2 sm:left-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-ink-900/60 hover:bg-ink-900/80 backdrop-blur-sm text-white flex items-center justify-center transition shadow-lg"
          >
            <svg width="16" height="16" viewBox="0 0 14 14" fill="none" aria-hidden>
              <path d="M9 1L3 7L9 13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onChange((index + 1) % total);
            }}
            aria-label="Next photo"
            className="absolute z-20 right-2 sm:right-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-ink-900/60 hover:bg-ink-900/80 backdrop-blur-sm text-white flex items-center justify-center transition shadow-lg"
          >
            <svg width="16" height="16" viewBox="0 0 14 14" fill="none" aria-hidden>
              <path d="M5 1L11 7L5 13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
        </>
      )}

      <figure
        className="relative max-w-[92vw] max-h-[88vh] flex flex-col items-center"
        onClick={(e) => e.stopPropagation()}
      >
        <img
          key={current.id}
          src={current.fullUrl}
          alt=""
          className="max-w-[92vw] max-h-[82vh] object-contain rounded-lg shadow-2xl"
        />
        {total > 1 && (
          <figcaption className="mt-3 text-xs text-white/70 font-mono tracking-wide">
            {index + 1} / {total}
          </figcaption>
        )}
      </figure>
    </div>,
    document.body
  );
}
